import { useState } from 'react';
import type { RewardStatus, EarnedBadge } from '@/lib/api';

interface BadgeCollectionModalProps {
  status: RewardStatus;
  onClose: () => void;
}

export default function BadgeCollectionModal({ status, onClose }: BadgeCollectionModalProps) {
  const [enlargedBadge, setEnlargedBadge] = useState<EarnedBadge | null>(null);
  const currentYear = new Date().getFullYear();
  const earnedBadges = status.earnedBadges || [];

  // Group badges by year
  const badgesByYear = earnedBadges.reduce((acc, badge) => {
    if (!acc[badge.year]) {
      acc[badge.year] = [];
    }
    acc[badge.year].push(badge);
    return acc;
  }, {} as Record<number, EarnedBadge[]>);

  // Sort years descending (newest first)
  const years = Object.keys(badgesByYear)
    .map(Number)
    .sort((a, b) => b - a);

  // Handle clicking on the current year badge (not in earnedBadges array)
  const handleCurrentYearBadgeClick = () => {
    const currentYearBadge: EarnedBadge = {
      id: 'current-year',
      tierLevel: status.currentYearTier?.level || status.currentTier.level,
      tierName: status.currentYearTier?.name || status.currentTier.name,
      badgeKey: status.currentYearTier?.badgeKey || status.currentTier.badgeKey,
      badgeUrl: status.currentYearTier?.badgeUrl || status.currentTier.badgeUrl,
      year: currentYear,
      earnedAt: new Date().toISOString(),
    };
    setEnlargedBadge(currentYearBadge);
  };

  return (
    <>
      {/* Enlarged Badge Overlay */}
      {enlargedBadge && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-[60] flex flex-col items-center justify-center cursor-pointer"
          onClick={() => setEnlargedBadge(null)}
        >
          <img
            src={enlargedBadge.badgeUrl}
            alt={enlargedBadge.tierName}
            className="w-[648px] max-w-[95vw] rounded-xl shadow-2xl object-contain"
            style={{ aspectRatio: '324/204' }}
          />
          <p className="mt-4 text-lg font-semibold text-white">{enlargedBadge.tierName}</p>
          <p className="text-sm text-gray-300">{enlargedBadge.year}</p>
          <p className="mt-6 text-sm text-gray-400">Click anywhere to go back</p>
        </div>
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Your Badge Collection</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Current Year Progress */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-amber-800 uppercase tracking-wide mb-3">
                {currentYear} Progress
              </h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleCurrentYearBadgeClick}
                  className="focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-md transition-transform hover:scale-105"
                >
                  <img
                    src={status.currentYearTier?.badgeUrl || status.currentTier.badgeUrl}
                    alt={status.currentYearTier?.name || status.currentTier.name}
                    className="w-20 h-12 rounded-md object-cover border border-amber-300 cursor-pointer"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </button>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    {status.currentYearTier?.name || status.currentTier.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {status.currentYearNights || 0} night{(status.currentYearNights || 0) !== 1 ? 's' : ''} this year
                  </p>
                </div>
              </div>
            </div>

            {/* Earned Badges by Year */}
            {years.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p>No badges earned yet.</p>
                <p className="text-sm mt-2">Stay more nights to earn your first badge!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {years.map((year) => (
                  <div key={year}>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <span className="bg-gray-100 px-2 py-0.5 rounded">{year}</span>
                      <span className="text-gray-400">
                        {badgesByYear[year].length} badge{badgesByYear[year].length !== 1 ? 's' : ''}
                      </span>
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {badgesByYear[year]
                        .sort((a, b) => b.tierLevel - a.tierLevel)
                        .map((badge) => (
                          <button
                            key={badge.id}
                            onClick={() => setEnlargedBadge(badge)}
                            className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-left transition-transform hover:scale-105 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-400"
                          >
                            <img
                              src={badge.badgeUrl}
                              alt={badge.tierName}
                              className="w-full h-auto rounded-md object-cover border border-gray-300 cursor-pointer"
                              style={{ aspectRatio: '324/204' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                if (target.parentElement) {
                                  const fallback = document.createElement('div');
                                  fallback.className = 'w-full rounded-md border border-gray-300 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center';
                                  fallback.style.aspectRatio = '324/204';
                                  fallback.innerHTML = '<span class="text-2xl">🏙️</span>';
                                  target.parentElement.insertBefore(fallback, target);
                                }
                              }}
                            />
                            <p className="mt-2 text-sm font-medium text-gray-900 text-center">
                              {badge.tierName}
                            </p>
                          </button>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={onClose}
              className="w-full btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
